import React, { useEffect, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { css } from '@emotion/react'
import styled from '@emotion/styled'
import { graphql } from 'gatsby'
import Seo from '~components/Seo'
import DataViewer from '~utils/DataViewer'
import useSiteSettings from '~utils/useSiteSettings'
import Section from '~components/Section'
import RichText from '~components/RichText'
import Image from '~components/Image'
import { mobile, tablet, UnderlineButton } from '~styles/global'
import ProductGrid from '~components/ProductGrid'
import Divider from '~components/Divider'
import Slider from '~components/Slider'
import RecipeTile from '~components/RecipeTile'
import Button from '~components/Button'
import HeaderSlider from '~components/HeaderSlider'

const IndexPage = ({ data }) => {
	const { page } = data
	// const [initial, setInitial] = useState(undefined)
	const scrollRef = useRef()

	const scrollDown = () => {
		const y = (scrollRef?.current?.getBoundingClientRect().top) - (window.pageYOffset)
		window.scrollTo({top: y, behavior: 'smooth'})
	}

	return (
		<>	
			<Seo 
				title={page.title}
				metaTitle={page.seo?.metaTitle}
				description={page.seo?.metaDescription}
				image={page.seo?.socialImage}
			/>
			<HeaderSlider slides={page.headerSlider} onScrollDown={() => scrollDown()}/>
			<div ref={scrollRef}/> 
			<FPHeader>
				<FPHeading>
					{page.featuredProductsTitle}
				</FPHeading>
				<FPSubheading>
					{page.featuredProductsSubtitle}
				</FPSubheading>
			</FPHeader>
			<FeaturedProducts products={page.featuredProducts}/>
			<Divider />
			<StorySection>
				<StoryTitle>
					{page.storyTitle}
				</StoryTitle>
				<StorySubtitle>
					{page.storySubtitle}
				</StorySubtitle>
				<StoryHeroImage image={page?.storyHeroImage} aspectRatio={1.72}/>
				<StorySlider slides={page.storyImageSlider} rounded/>
				<StoryText>
					<RichText content={page.storyText}/>
				</StoryText>
			</StorySection>
			<Divider />
			<FRSection>
				<FRTitle>{page.featuredRecipesTitle}</FRTitle>
				<FRSubtitle>{page.featuredRecipesSubtitle}</FRSubtitle>
				{page.featuredRecipes.map(r => (
					<StyledRecipeTile recipe={r} key={r.title}/>
				))}
				<FRButtonRow>
					{data?.recipePage?.slug?.current}
					<FRButton sm 
						link={{
							linkType: 'internal', 
							document: {
								slug: data?.recipesPage?.slug
							}
						}}>
					View all
					</FRButton>
				</FRButtonRow>
			</FRSection>
			<Divider />
			<SusSection>
				<SusTitle>
					{page.sustainabilityTitle}
				</SusTitle>
				<SusSubtitle>
					{page.sustainabilitySubtitle}
				</SusSubtitle>
				<SusImage image={page.sustainabilityImage}/>
				<SusButtonRow>
					<SusButton link={page.sustainabilityLink?.link} className='h6'>
						{page.sustainabilityLink?.text}
					</SusButton>
				</SusButtonRow>
			</SusSection>
			<DataViewer data={data} name="page"/>
		</>
	)
}


const FPHeader = styled(Section)`
	padding-top: var(--xl);
	margin-bottom: var(--xxl);
`
const FPHeading = styled.h6`
	grid-column: span 12;
	text-align: center;
	margin-bottom: var(--l);
`
const FPSubheading = styled.h3`
	grid-column: span 12;
	text-align: center;
`
const FeaturedProducts = styled(ProductGrid)`
	margin-bottom: var(--xxxxl);
	${mobile}{
		margin-bottom: var(--xxxl);
	}
`
const StorySection = styled(Section)`
	
`
const StoryTitle = styled.h6`
	grid-column: span 12;
	padding-top: var(--xl);
	padding-bottom: var(--l);
	text-align: center;
`
const StorySubtitle = styled.h3`
	grid-column: span 12;
	text-align: center;
	max-width: 420px;
	margin: 0 auto var(--xxl);
`
const StoryHeroImage = styled(Image)`
	grid-column: 3/11;
	margin-bottom: var(--xxl);
	border-radius: 10px;
	overflow: hidden;
	${mobile}{
		grid-column: span 12;
		margin-bottom: var(--xxxl);
	}
`
const StorySlider = styled(Slider)`
	grid-column: 2/6;
	margin-bottom: var(--xxxxl);
	${tablet}{
		grid-column: 1/7;
	}
	${mobile}{
		grid-column: 2/12;
		margin-bottom: var(--xxxl);
	}
`
const StoryText = styled.div`
	grid-column: 7/12;
	margin-bottom: var(--xxxxl);
	${tablet}{
		grid-column: 7/13;
	}
	${mobile}{
		grid-column: 2/12;
		grid-row: 4;
		margin-bottom: var(--xxxl);
	}
`
const FRSection = styled(Section)`
	margin-top: var(--xxxxl);
	margin-bottom: var(--xl);
	${mobile}{
		margin-top: var(--xxxl);
		margin-bottom: var(--xxxl);
	}
`
const FRTitle = styled.h6`
	grid-column: span 12;
	text-align: center;
	margin-bottom: var(--s);
`
const FRSubtitle = styled.h3`
	grid-column: span 12;
	text-align: center;
	max-width: 420px;
	margin: 0 auto var(--xxxxl);
	${mobile}{
		margin-bottom: var(--xxxl);
	}
`
const StyledRecipeTile = styled(RecipeTile)`
	grid-column: span 6;	
`
const FRButtonRow = styled.div`
	grid-column: span 12;
`
const FRButton = styled(Button)`
	margin: var(--xxl) auto 0;
`
const SusSection = styled(Section)`
	margin-top: var(--xxxl);
`
const SusTitle = styled.h6`
	grid-column: span 12;
	text-align: center;
	margin-bottom: var(--l);
`
const SusSubtitle = styled.h3`
	grid-column: span 12;
	text-align: center;
	max-width: 420px;
	margin: 0 auto var(--xl);
`
const SusImage = styled(Image)`
	grid-column: 5/9;
	margin-bottom: var(--xl);
	${tablet}{
		grid-column: 4/10;
	}
`
const SusButtonRow = styled.div`
	grid-column: span 12;
	text-align: center;
	margin-bottom: var(--xxxl);
`
const SusButton = styled(UnderlineButton)`
	margin: 0 auto;
	bottom: -0.6em;
`
export const query = graphql`
  query HomeQuery {
    page: sanityHomePage {
      title
			headerSlider {
				imageDesktop {
					...imageWithAlt
				}
				imageMobile {
					...imageWithAlt
				}
				headline: _rawHeadline
				buttonText
				triggerSlideDown
				link {
					...link
				}
			}
			featuredProductsTitle
			featuredProductsSubtitle
			featuredProducts {
				...productThumb
			}
			storyTitle
			storySubtitle
			storyHeroImage {
				...imageWithAlt
			}
			storyImageSlider {
				...imageWithCaption
			}
			storyText: _rawStoryText
			featuredRecipesTitle
			featuredRecipesSubtitle
			featuredRecipes {
				...recipeThumb
			}
			sustainabilityTitle
			sustainabilitySubtitle
			sustainabilityImage {
				...imageWithAlt
			}
			sustainabilityLink {
				...button
			}
			seo{
				...seo
			}
    }
		recipesPage: sanityRecipesPage {
			slug {
				current
			}
		}
  }
`

IndexPage.propTypes = {
	data: PropTypes.object,
}

export default IndexPage
